import React from "react";

/**
 * Top-level error boundary. Catches any uncaught render-time error in a child
 * route and shows a friendly message + the actual error text so the user (and
 * support) can read the cause instead of seeing an empty black screen.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null, info: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        this.setState({ info });
        // eslint-disable-next-line no-console
        console.error("[ErrorBoundary] React render error:", error, info);
    }

    handleReload = () => {
        this.setState({ error: null, info: null });
        window.location.reload();
    };

    handleGoHome = () => {
        window.location.href = "/";
    };

    render() {
        const { error, info } = this.state;
        if (!error) return this.props.children;

        const stack = (info && info.componentStack) || "";
        return (
            <div className="min-h-screen bg-[#FAFAFA] text-zinc-950 flex items-center justify-center p-6 font-body" data-testid="error-boundary">
                <div className="max-w-2xl w-full bg-[#FFFFFF] border border-zinc-200 rounded-md p-6 lg:p-8">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-red-600 font-bold mb-2">Onverwachte fout</div>
                    <h1 className="font-display font-black text-3xl tracking-tight mb-3">Er liep iets mis bij het laden van deze pagina.</h1>
                    <p className="text-sm text-zinc-500 mb-5">
                        Probeer de pagina opnieuw te laden. Werkt het nog niet, deel dan de onderstaande foutmelding met Social Factory.
                    </p>
                    <pre className="bg-black/40 border border-white/5 rounded-sm p-3 text-[11px] text-red-300 whitespace-pre-wrap break-words max-h-72 overflow-auto" data-testid="error-boundary-message">
                        {String(error?.message || error)}
                        {stack ? `\n\n${stack}` : ""}
                    </pre>
                    <div className="flex items-center gap-2 mt-5">
                        <button
                            onClick={this.handleReload}
                            className="bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm h-10 px-4 text-sm transition active:scale-[0.98]"
                            data-testid="error-boundary-reload"
                        >
                            Pagina opnieuw laden
                        </button>
                        <button
                            onClick={this.handleGoHome}
                            className="border border-zinc-200 text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100 rounded-sm h-10 px-4 text-sm transition"
                            data-testid="error-boundary-home"
                        >
                            Naar overzicht
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}
